import React, { FC, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { Divider } from '@material-ui/core'
import Theme from 'core/themes/model'
import clsx from 'clsx'
import FontAwesomeIcon from '../FontAwesomeIcon'
import Text from 'core/elements/Text'

const useIconInfoStyles = makeStyles<Theme, { spacer: boolean }>((theme: Theme) => ({
  alertTitle: {
    display: 'flex',
    alignItems: 'center',

    '& i': {
      color: theme.components.graph.primary,
      fontSize: 22,
      marginRight: 4,
    },
  },
  infoContainer: {
    margin: ({ spacer }) => (spacer ? '60px 0 40px 0' : '16px 0'),
  },
}))

const useStyles = makeStyles<Theme, { error: boolean; warning: boolean }>((theme: Theme) => ({
  container: {
    border: ({ error, warning }) =>
      `1px solid ${
        error
          ? theme.palette.red[500]
          : warning
          ? theme.palette.yellow[500]
          : theme.components.table.border
      }`,
    background: ({ error, warning }) =>
      error ? theme.palette.red[100] : warning ? theme.palette.yellow[100] : 'transparent',
    borderRadius: 4,
    ...theme.typography.body2,
    color: theme.components.card.text,
  },
  minimizedContainer: {
    border: `1px solid ${theme.components.table.border}`,
    borderRadius: 4,
  },
  header: {
    background: theme.components.table.hoverBackground,
    padding: theme.spacing(1.5, 3),
    fontSize: 16,
    color: theme.components.card.text,
    fontWeight: 600,
    display: 'flex',
    cursor: 'pointer',
  },
  title: {
    flexGrow: 1,
    alignSelf: 'center',
  },
  button: {
    flexGrow: 0,
    height: '24px',
    width: '24px',
    borderRadius: '50%',
    display: 'flex',
  },
  icon: {
    color: theme.components.card.text,
    alignSelf: 'center',
    marginLeft: '4px',
  },
  content: {
    padding: theme.spacing(1.5, 3),
  },
}))

interface Props {
  children: any
  error?: boolean
  warning?: boolean
  className?: string
  title?: string
  expanded?: boolean // Show expanded by default or not
}

const Info = ({
  children,
  error = false,
  warning = false,
  className = undefined,
  title = '',
  expanded = true,
}: Props) => {
  const classes = useStyles({ error, warning })
  const [isExpanded, setExpanded] = useState(expanded)
  const isMinimized = title && !isExpanded

  return (
    <div className={clsx(isMinimized ? classes.minimizedContainer : classes.container, className)}>
      {title && (
        <div
          className={classes.header}
          onClick={() => {
            setExpanded(!isExpanded)
          }}
        >
          <span className={classes.title}>{title}</span>
          <div className={classes.button}>
            <FontAwesomeIcon className={classes.icon}>
              {`angle-${isExpanded ? 'up' : 'down'}`}
            </FontAwesomeIcon>
          </div>
        </div>
      )}
      {title && isExpanded && <Divider />}
      {(!title || isExpanded) && <div className={classes.content}>{children}</div>}
    </div>
  )
}

export const IconInfo: FC<{
  icon: string
  title: string | React.ReactNode
  spacer?: boolean
  className?: string
  iconClass?: string
  children?: React.ReactNode
}> = ({ icon, title, children, className = '', iconClass = '', spacer = true }) => {
  const classes = useIconInfoStyles({ spacer })
  return (
    <Info className={clsx(classes.infoContainer, className)}>
      <Text className={classes.alertTitle} variant="body2">
        <FontAwesomeIcon className={iconClass}>{icon}</FontAwesomeIcon> {title}
      </Text>
      {!!children && <br />}
      {children}
    </Info>
  )
}

export default Info
